import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Lightweight scroll reveal wrapper using IntersectionObserver.
 * - Respects prefers-reduced-motion via CSS in `Home.css` / `App.css`.
 * - `delayMs` lets you stagger reveals inside grids.
 */
export default function Reveal({
  as: Comp = "div",
  className = "",
  delayMs = 0,
  once = true,
  children,
  ...props
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const style = useMemo(() => {
    if (!delayMs) return props.style;
    return { ...(props.style || {}), "--reveal-delay": `${delayMs}ms` };
  }, [delayMs, props.style]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If browser doesn't support IntersectionObserver, just show content.
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) io.disconnect();
          } else if (!once) {
            setIsVisible(false);
          }
        }
      },
      { root: null, threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  return (
    <Comp
      ref={ref}
      className={`reveal ${isVisible ? "is-visible" : ""} ${className}`.trim()}
      style={style}
      {...props}
    >
      {children}
    </Comp>
  );
}

